import { c as _c } from "react/compiler-runtime";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/blacksmith-ui/avatar";
import { useActiveGithubOrgForUserNonSuspense, useConnectedGithubOrgsNonSuspense, useIsImpersonatingNonSuspense, type SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { githubReconnectLogin, markSsoReauthPending } from "@/lib/github-reauth";
import { githubAppInstallUrl, instanceBasePath, namedOrgKey, type NamedOrg } from "@/lib/named-org";
import { orgTargetPath } from "@/lib/orgTargetPath";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { useNewOrgToast } from "@/hooks/useNewOrgToast";
import { useSsoReauthPrompt } from "@/hooks/useSsoReauthPrompt";
import { AuthContext } from "@/components/AuthProvider";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { safeLocalStorage } from "@/lib/storage";
import { cn } from "@repo/blacksmith-ui/cn";
import { ChevronRight, ShieldAlert, Sparkles, Check, Plus, RefreshCw } from "lucide-react";
import { useTheme } from "next-themes";
import Link from "@/components/DashboardLink";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useMemo, useState, useEffect, useCallback, useContext, useRef } from "react";
import { BlackGitHubIcon, GitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { githubLoginOnInstance } from "@/lib/github-login";
const EMPTY_ORGS: string[] = [];
export interface OrgSelectorOrg {
  login: string;
  avatarUrl: string;
  isNew?: boolean;
  isSuspended?: boolean;
  /** Route to navigate to when this org is selected. When set, the row
   * renders as a real link so modified clicks open in a new tab. */
  href?: string;
}
export interface GitHubOrgSelectorViewProps {
  orgs: OrgSelectorOrg[];
  activeOrgLogin?: string | null;
  /**
   * Orgs GitHub hid from the user's account because their OAuth token lacks
   * an active SAML SSO authorization. Surfaced as an amber notification dot
   * on the trigger and highlighted rows in the dropdown that deep-link to
   * GitHub's SSO authorization flow.
   */
  ssoBlockedOrgs?: SsoBlockedOrg[];
  isImpersonating?: boolean;
  /** Selection callback for org rows without an `href`; rows with an `href`
   * navigate via the link itself. */
  onSelectOrg?: (login: string) => void;
  onAddOrg: () => void;
  /** GitHub App installation URL for the instance being viewed; when set the
   * "Add an organization" menu item renders as a real link so modified clicks
   * open in a new tab. */
  addOrgHref?: string;
  onAuthorizeOrg?: (org: SsoBlockedOrg) => void;
  /**
   * Blocked orgs the user has already clicked Authorize for. Their row swaps
   * Authorize for Reconnect (the silent OAuth re-login that actually restores
   * access); once the re-login lands the flag is gone, so an org that is still
   * blocked falls back to Authorize.
   */
  ssoReauthPendingOrgs?: string[];
  onReconnect?: () => void;
  /**
   * Whether the enclosing app-switcher rail is expanded. When collapsed the
   * trigger's border fades out so the org avatar reads as a bare icon in the
   * slim rail; it fades back in as the rail wipes open. Defaults to `true` so
   * standalone/story usages keep the bordered pill.
   */
  expanded?: boolean;
  /**
   * True while org data is still being fetched. `orgs` is empty during this
   * window too, so without this flag the empty-state ("Add an organization",
   * which renders a GitHub glyph) flashes on every refresh before the real
   * org avatar arrives. When loading, render the bordered pill with the
   * active org login when one is known (org routes), or a skeleton pill when
   * there is nothing to show yet (org-less routes).
   */
  isLoading?: boolean;
  /** Superuser viewing an org on an instance they have no GitHub login on. */
  superuserWithoutInstanceLogin?: boolean;
  instanceHost?: string;
}
export function GitHubOrgSelectorView(t0) {
  const $ = _c(78);
  const {
    orgs,
    activeOrgLogin,
    ssoBlockedOrgs: t1,
    isImpersonating: t2,
    onSelectOrg,
    onAddOrg,
    addOrgHref,
    onAuthorizeOrg,
    ssoReauthPendingOrgs: t3,
    onReconnect,
    expanded: t4,
    isLoading: t5,
    superuserWithoutInstanceLogin: t6,
    instanceHost
  } = t0;
  let t7;
  if ($[0] !== t1) {
    t7 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const ssoBlockedOrgs = t7;
  const isImpersonating = t2 === undefined ? false : t2;
  const ssoReauthPendingOrgs = t3 === undefined ? EMPTY_ORGS : t3;
  const expanded = t4 === undefined ? true : t4;
  const isLoading = t5 === undefined ? false : t5;
  const superuserWithoutInstanceLogin = t6 === undefined ? false : t6;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const {
    resolvedTheme: theme
  } = useTheme();
  let t8;
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleOpenOrgSwitcher = () => {
        setIsOpen(true);
      };
      window.addEventListener("openOrgSwitcher", handleOpenOrgSwitcher);
      return () => {
        window.removeEventListener("openOrgSwitcher", handleOpenOrgSwitcher);
      };
    };
    t9 = [];
    $[2] = t8;
    $[3] = t9;
  } else {
    t8 = $[2];
    t9 = $[3];
  }
  useEffect(t8, t9);
  let t10;
  if ($[4] !== activeOrgLogin || $[5] !== orgs) {
    t10 = orgs.find(org => org.login === activeOrgLogin) ?? orgs.at(0);
    $[4] = activeOrgLogin;
    $[5] = orgs;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const activeOrg = t10;
  let t11;
  if ($[7] !== orgs) {
    t11 = orgs.some(_temp);
    $[7] = orgs;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const hasNewInstallations = t11;
  const hasSsoBlockedOrgs = ssoBlockedOrgs.length > 0;
  if (!orgs.length && isLoading && !activeOrgLogin && !isImpersonating) {
    let t12;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Skeleton className="h-8 w-full rounded-lg" />;
      $[9] = t12;
    } else {
      t12 = $[9];
    }
    return t12;
  }
  if (!orgs.length && !isLoading && !isImpersonating) {
    let t12;
    if ($[10] !== theme) {
      t12 = <div className="shrink-0">{theme === "dark" ? <WhiteGitHubIcon /> : <BlackGitHubIcon />}</div>;
      $[10] = theme;
      $[11] = t12;
    } else {
      t12 = $[11];
    }
    let t13;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <span className="justify-start w-40 truncate text-neutral-900 dark:text-neutral-100">Add an organization</span>;
      $[12] = t13;
    } else {
      t13 = $[12];
    }
    let t14;
    if ($[13] !== t12) {
      t14 = <DropdownMenuTrigger className="focus-visible:outline-hidden"><span className="flex items-center">{t12}{t13}</span></DropdownMenuTrigger>;
      $[13] = t12;
      $[14] = t14;
    } else {
      t14 = $[14];
    }
    let t15;
    if ($[15] !== isOpen || $[16] !== t14) {
      t15 = <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>{t14}</DropdownMenu>;
      $[15] = isOpen;
      $[16] = t14;
      $[17] = t15;
    } else {
      t15 = $[17];
    }
    let t16;
    if ($[18] !== onAddOrg || $[19] !== t15) {
      t16 = <div className="rounded-xl border-[1px] border-[var(--color-border)] px-3 py-2 text-neutral-500 text-sm font-medium w-50" onClick={onAddOrg}>{t15}</div>;
      $[18] = onAddOrg;
      $[19] = t15;
      $[20] = t16;
    } else {
      t16 = $[20];
    }
    return t16;
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = open => {
      setIsOpen(open);
      if (!open) {
        setTimeout(() => {
          triggerRef.current?.blur();
        }, 0);
      }
    };
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const handleOpenChange = t12;
  const t13 = expanded ? "border-[#0c0c0d21] dark:border-white/16" : "border-transparent dark:border-transparent";
  let t14;
  if ($[22] !== t13) {
    t14 = cn("relative flex items-center w-full h-8 gap-2 px-1 overflow-hidden rounded-lg border bg-transparent focus-visible:outline-hidden cursor-pointer", "[transition:border-color_240ms_cubic-bezier(0.22,1,0.36,1)]", t13);
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== activeOrg?.avatarUrl || $[25] !== activeOrg?.login || $[26] !== activeOrgLogin || $[27] !== isImpersonating) {
    t15 = <Avatar className="size-[22px] shrink-0 border border-[#0c0c0d21] dark:border-white/24">{isImpersonating ? <ShieldAlert className="aspect-square h-full w-full text-orange-500" /> : <><AvatarImage src={activeOrg?.avatarUrl ?? ""} alt="active org logo" loading="eager" /><AvatarFallback className="uppercase">{(activeOrg?.login ?? activeOrgLogin)?.charAt(0)}</AvatarFallback></>}</Avatar>;
    $[24] = activeOrg?.avatarUrl;
    $[25] = activeOrg?.login;
    $[26] = activeOrgLogin;
    $[27] = isImpersonating;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = isImpersonating && "text-orange-500 font-bold";
  let t17;
  if ($[29] !== t16) {
    t17 = cn("flex-1 text-left truncate type-body-s-regular text-neutral-950 dark:text-white", t16);
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = (isImpersonating ? activeOrgLogin : activeOrg?.login ?? activeOrgLogin) ?? "";
  let t19;
  if ($[31] !== t17 || $[32] !== t18) {
    t19 = <span className={t17}>{t18}</span>;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ChevronRight className="size-[18px] shrink-0 rotate-90 text-neutral-500 dark:text-neutral-400" />;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t19) {
    t21 = <DropdownMenuTrigger ref={triggerRef} className={t14}>{t15}{t19}{t20}</DropdownMenuTrigger>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t19;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      triggerRef.current?.blur();
    };
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== addOrgHref || $[41] !== instanceHost || $[42] !== onAddOrg || $[43] !== superuserWithoutInstanceLogin) {
    t23 = superuserWithoutInstanceLogin ? <DropdownMenuLabel variant="heading" className="px-2 py-2"><span className="flex items-center gap-1.5 type-body-xs-medium text-orange-500"><ShieldAlert className="size-3.5 shrink-0" />Viewing as superuser</span><span className="mt-1 block type-body-xxs-regular text-neutral-500 dark:text-neutral-300">Your account isn't linked to{" "}{instanceHost ?? "this GitHub instance"}, so its organizations aren't listed.</span></DropdownMenuLabel> : addOrgHref ? <DropdownMenuItem key="add-new-org" asChild={true} icon={<Plus />}><a href={addOrgHref} onClick={() => setIsOpen(false)}>Add an organization</a></DropdownMenuItem> : <DropdownMenuItem key="add-new-org" icon={<Plus />} onClick={() => {
      setIsOpen(false);
      onAddOrg();
    }}>Add an organization</DropdownMenuItem>;
    $[40] = addOrgHref;
    $[41] = instanceHost;
    $[42] = onAddOrg;
    $[43] = superuserWithoutInstanceLogin;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== superuserWithoutInstanceLogin) {
    t24 = !superuserWithoutInstanceLogin && <DropdownMenuSeparator />;
    $[45] = superuserWithoutInstanceLogin;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== activeOrgLogin || $[48] !== onSelectOrg || $[49] !== orgs) {
    let t26;
    if ($[51] !== activeOrgLogin || $[52] !== onSelectOrg) {
      t26 = org_1 => {
        const isActive = org_1.login === activeOrgLogin;
        const rowOptions = {
          icon: <Avatar className="size-[18px] bg-neutral-100 dark:bg-neutral-700"><AvatarImage src={org_1.avatarUrl} alt={`${org_1.login} logo`} loading="eager" /><AvatarFallback className="uppercase text-[10px] text-neutral-900 dark:text-white">{org_1.login?.charAt(0)}</AvatarFallback></Avatar>,
          trailing: isActive || org_1.isNew || org_1.isSuspended ? <span className="flex items-center gap-2">{org_1.isNew && <span className="flex items-center gap-1 type-body-xs-regular text-green-600 dark:text-green-400"><Sparkles className="size-3" />New</span>}{org_1.isSuspended && <span className="type-body-xs-regular text-red-500">Suspended</span>}{isActive && <Check className="size-3.5 text-neutral-900 dark:text-white" strokeWidth={2.25} />}</span> : undefined
        };
        const rowClassName = cn(isActive && "font-medium");
        return org_1.href ? <DropdownMenuItem key={org_1.login} asChild={true} className={rowClassName} {...rowOptions}><Link href={org_1.href} instanceSlug={null} onClick={() => setIsOpen(false)}>{org_1.login}</Link></DropdownMenuItem> : <DropdownMenuItem key={org_1.login} className={rowClassName} {...rowOptions} onClick={() => {
          setIsOpen(false);
          onSelectOrg?.(org_1.login);
        }}>{org_1.login}</DropdownMenuItem>;
      };
      $[51] = activeOrgLogin;
      $[52] = onSelectOrg;
      $[53] = t26;
    } else {
      t26 = $[53];
    }
    t25 = orgs.map(t26);
    $[47] = activeOrgLogin;
    $[48] = onSelectOrg;
    $[49] = orgs;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[54] !== hasSsoBlockedOrgs || $[55] !== onAuthorizeOrg || $[56] !== onReconnect || $[57] !== ssoBlockedOrgs || $[58] !== ssoReauthPendingOrgs) {
    t26 = hasSsoBlockedOrgs && <><DropdownMenuSeparator /><div className="flex items-center gap-1.5 px-2 pt-2 pb-1 type-body-xxs-regular text-amber-600 dark:text-amber-400"><ShieldAlert className="w-3 h-3 shrink-0" />Hidden until you re-authorize SSO</div>{ssoBlockedOrgs.map(org_2 => {
        const reconnectPending = ssoReauthPendingOrgs.includes(org_2.name);
        return <DropdownMenuItem key={org_2.name} className="bg-amber-500/10 focus:bg-amber-500/20 data-[highlighted]:bg-amber-500/20 dark:bg-amber-400/10 dark:focus:bg-amber-400/20 dark:data-[highlighted]:bg-amber-400/20" icon={<span className="flex size-[18px] items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-500/50"><span className="uppercase text-[10px] text-amber-600 dark:text-amber-400">{org_2.name.charAt(0)}</span></span>} trailing={<span className="flex items-center gap-1 type-body-xs-regular text-amber-600 dark:text-amber-400">{reconnectPending ? <>Reconnect<RefreshCw className="size-3" /></> : <>Authorize<GitHubIcon className="size-3" /></>}</span>} onClick={() => {
          if (reconnectPending) {
            onReconnect?.();
            return;
          }
          window.open(org_2.sso_url, "_blank", "noopener,noreferrer");
          onAuthorizeOrg?.(org_2);
        }}>{org_2.name}</DropdownMenuItem>;
      })}</>;
    $[54] = hasSsoBlockedOrgs;
    $[55] = onAuthorizeOrg;
    $[56] = onReconnect;
    $[57] = ssoBlockedOrgs;
    $[58] = ssoReauthPendingOrgs;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== superuserWithoutInstanceLogin) {
    t27 = !superuserWithoutInstanceLogin && <><DropdownMenuSeparator /><div className="px-2 py-2 type-body-xxs-regular text-neutral-950 dark:text-white text-center">Missing an org?{" "}<a href="https://docs.blacksmith.sh/introduction/quickstart#dont-see-your-organization-in-the-blacksmith-dashboard" target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-neutral-100 underline underline-offset-2 hover:text-neutral-700 dark:hover:text-neutral-300">Here's why!</a></div></>;
    $[60] = superuserWithoutInstanceLogin;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t23 || $[63] !== t24 || $[64] !== t25 || $[65] !== t26 || $[66] !== t27) {
    t28 = <DropdownMenuContent align="start" side="bottom" sideOffset={0} alignOffset={0} className="w-[239px]" onInteractOutside={t22}>{t23}{t24}{t25}{t26}{t27}</DropdownMenuContent>;
    $[62] = t23;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== isOpen || $[69] !== t21 || $[70] !== t28) {
    t29 = <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} modal={false}>{t21}{t28}</DropdownMenu>;
    $[68] = isOpen;
    $[69] = t21;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== hasNewInstallations || $[73] !== hasSsoBlockedOrgs) {
    t30 = hasSsoBlockedOrgs ? <div className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" /></div> : hasNewInstallations ? <div className="pointer-events-none absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" /></div> : null;
    $[72] = hasNewInstallations;
    $[73] = hasSsoBlockedOrgs;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t29 || $[76] !== t30) {
    t31 = <div className="relative w-full text-sm font-medium">{t29}{t30}</div>;
    $[75] = t29;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  return t31;
}
function _temp(org_0) {
  return org_0.isNew;
}
export function GitHubOrgSelector(t0) {
  const $ = _c(46);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    expanded: t2
  } = t1;
  const expanded = t2 === undefined ? true : t2;
  const router = useRouter();
  const {
    connectedGithubOrgs,
    isLoading
  } = useConnectedGithubOrgsNonSuspense();
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const isImpersonating = useIsImpersonatingNonSuspense();
  const {
    user
  } = useContext(AuthContext);
  const urlSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  const instanceSlug = connectedGithubOrgs?.instance_slug ?? urlSlug;
  let t3;
  if ($[2] !== instanceSlug) {
    t3 = githubAppInstallUrl(instanceSlug);
    $[2] = instanceSlug;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const addOrgHref = t3;
  let t4;
  if ($[4] !== instanceSlug || $[5] !== isImpersonating || $[6] !== user) {
    t4 = isImpersonating && githubLoginOnInstance(user, instanceSlug) === null;
    $[4] = instanceSlug;
    $[5] = isImpersonating;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const superuserWithoutInstanceLogin = t4;
  let t5;
  if ($[8] !== connectedGithubOrgs?.github_web_url) {
    t5 = connectedGithubOrgs?.github_web_url?.replace(/^https?:\/\//, "");
    $[8] = connectedGithubOrgs?.github_web_url;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const instanceHost = t5;
  let t6;
  if ($[10] !== connectedGithubOrgs?.sso_blocked_orgs) {
    t6 = connectedGithubOrgs?.sso_blocked_orgs ?? [];
    $[10] = connectedGithubOrgs?.sso_blocked_orgs;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const ssoBlockedOrgs = t6;
  const ssoReauthPendingOrgs = useSsoReauthPrompt(connectedGithubOrgs ? ssoBlockedOrgs : undefined);
  const getVisitedOrgs = _temp2;
  let t7;
  let t8;
  if ($[12] !== activeNamedOrg) {
    t7 = () => {
      if (activeNamedOrg) {
        const visited = getVisitedOrgs();
        visited.add(namedOrgKey(activeNamedOrg));
        safeLocalStorage.set("blacksmith_visited_orgs", JSON.stringify(Array.from(visited)));
      }
    };
    t8 = [activeNamedOrg];
    $[12] = activeNamedOrg;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (org, createdAt) => {
      const createdDate = new Date(createdAt);
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 900000);
      const isRecent = createdDate > fifteenMinutesAgo;
      const isVisited = getVisitedOrgs().has(namedOrgKey(org));
      return isRecent && !isVisited;
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const isNewInstallation = t9;
  const routePattern = router.pathname;
  let t10;
  if ($[16] !== connectedGithubOrgs?.installations) {
    t10 = connectedGithubOrgs?.installations ?? [];
    $[16] = connectedGithubOrgs?.installations;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const installations = t10;
  let t11;
  if ($[18] !== activeNamedOrg || $[19] !== installations || $[20] !== routePattern) {
    let t12;
    if ($[22] !== activeNamedOrg || $[23] !== installations.length || $[24] !== routePattern) {
      t12 = org_0 => {
        const namedOrg = {
          orgName: org_0.account.login,
          instanceSlug: org_0.instance_slug
        };
        return {
          login: org_0.account.login,
          org: namedOrg,
          avatarUrl: org_0.account.avatar_url,
          href: `${instanceBasePath(org_0.instance_slug)}${orgTargetPath(routePattern, org_0.account.login)}`,
          isNew: installations.length > 1 && namedOrgKey(namedOrg) !== namedOrgKey(activeNamedOrg) && org_0.account.type !== "User" && isNewInstallation(namedOrg, org_0.created_at),
          isSuspended: org_0.suspended ?? Boolean(org_0.suspended_by)
        };
      };
      $[22] = activeNamedOrg;
      $[23] = installations.length;
      $[24] = routePattern;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    t11 = installations.map(t12);
    $[18] = activeNamedOrg;
    $[19] = installations;
    $[20] = routePattern;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const orgs = t11;
  let t12;
  if ($[26] !== connectedGithubOrgs || $[27] !== orgs) {
    t12 = connectedGithubOrgs ? orgs.filter(_temp3).map(_temp4) : undefined;
    $[26] = connectedGithubOrgs;
    $[27] = orgs;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const newOrgs = t12;
  let t13;
  if ($[29] !== orgs || $[30] !== router) {
    t13 = target => {
      const href = orgs.find(org_3 => namedOrgKey(org_3.org) === namedOrgKey(target))?.href;
      if (href) {
        router.push(href);
      }
    };
    $[29] = orgs;
    $[30] = router;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const switchToOrg = t13;
  useNewOrgToast(newOrgs, switchToOrg);
  const t14 = activeNamedOrg?.orgName;
  let t15;
  if ($[32] !== addOrgHref) {
    t15 = () => {
      window.location.href = addOrgHref;
    };
    $[32] = addOrgHref;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== addOrgHref || $[35] !== expanded || $[36] !== instanceHost || $[37] !== isImpersonating || $[38] !== isLoading || $[39] !== orgs || $[40] !== ssoBlockedOrgs || $[41] !== ssoReauthPendingOrgs || $[42] !== superuserWithoutInstanceLogin || $[43] !== t14 || $[44] !== t15) {
    t16 = <GitHubOrgSelectorView orgs={orgs} activeOrgLogin={t14} ssoBlockedOrgs={ssoBlockedOrgs} isImpersonating={isImpersonating} isLoading={isLoading} expanded={expanded} superuserWithoutInstanceLogin={superuserWithoutInstanceLogin} instanceHost={instanceHost} addOrgHref={addOrgHref} onAddOrg={t15} onAuthorizeOrg={_temp5} ssoReauthPendingOrgs={ssoReauthPendingOrgs} onReconnect={githubReconnectLogin} />;
    $[34] = addOrgHref;
    $[35] = expanded;
    $[36] = instanceHost;
    $[37] = isImpersonating;
    $[38] = isLoading;
    $[39] = orgs;
    $[40] = ssoBlockedOrgs;
    $[41] = ssoReauthPendingOrgs;
    $[42] = superuserWithoutInstanceLogin;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}
function _temp5(org_4) {
  return markSsoReauthPending(org_4.name);
}
function _temp4(org_2) {
  return org_2.org;
}
function _temp3(org_1) {
  return org_1.isNew;
}
function _temp2() {
  const stored = safeLocalStorage.get("blacksmith_visited_orgs");
  if (!stored) {
    return new Set();
  }
  try {
    return new Set(JSON.parse(stored));
  } catch {
    return new Set();
  }
}